import { AxiosError } from 'axios';
import { useTranslation } from 'react-i18next';
import { useDialog } from '../context/DialogContext';
import log from './logger';

export type ApiErrorResponse = {
  error?: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
  };
};

/** Thrown when a GenericApiResponse carries an error payload (or is missing data). */
export class ApiClientError extends Error {
  code: string;

  details?: Record<string, unknown>;

  constructor(code: string, message: string, details?: Record<string, unknown>) {
    super(message);
    this.name = 'ApiClientError';
    this.code = code;
    this.details = details;
  }
}

/** Oral exam join errors that are expected user-facing states, not Sentry-worthy bugs. */
export const ORAL_EXAM_JOIN_EXPECTED_ERROR_CODES = [
  'too_early',
  'late_join_cutoff',
  'session_started_without_you',
  'expired',
  'session_already_ended',
  'wrong_provider',
  'oral_exam_access_denied',
  'oral_exam_booking_not_found',
  'oral_exam_slot_not_found',
] as const;

/**
 * Unwrap a successful GenericApiResponse body or throw ApiClientError.
 * Axios failures (4xx/5xx) are handled separately via processApiError.
 */
export function unwrapApiResponseData<T>(
  response: { data?: { data?: T | null; error?: ApiErrorResponse['error'] | null } },
): T {
  const body = response.data;
  if (body?.error) {
    throw new ApiClientError(body.error.code, body.error.message, body.error.details);
  }
  if (body?.data == null) {
    throw new ApiClientError('UNKNOWN_ERROR', 'Missing response data');
  }
  return body.data;
}

export type ApiErrorDisplayMode = 'toast' | 'dialog';

export type ApiErrorReportOptions = {
  /**
   * Error codes that represent expected user validation failures.
   * These will be logged at 'warn' level (console only) instead of 'error' (Sentry).
   *
   * Example: ['AUTH_PASSWORD_POLICY_VIOLATED', 'AUTH_INVALID_CREDENTIALS']
   */
  expectedErrorCodes?: string[];
};

/**
 * Processes an API error and returns formatted information
 */
export function processApiError(error: unknown): {
  code: string;
  message: string;
  details?: Record<string, unknown>;
  originalError: unknown;
} {
  // Default error information
  const result: {
    code: string;
    message: string;
    details?: Record<string, unknown>;
    originalError: unknown;
  } = {
    code: 'UNKNOWN_ERROR',
    message: 'An unexpected error occurred',
    details: undefined,
    originalError: error,
  };

  if (error instanceof ApiClientError) {
    result.code = error.code;
    result.message = error.message;
    result.details = error.details;
  } else if (error instanceof AxiosError) {
    // Extract error details from the response if available
    const apiError = error.response?.data as ApiErrorResponse | undefined;

    if (apiError?.error) {
      result.code = apiError.error.code;
      result.message = apiError.error.message;
      result.details = apiError.error.details;

      // Detect specific error patterns and assign more specific error codes
      if (result.code === 'VALIDATION_ERROR') {
        // Check for task objectives validation error
        if (result.details?.invalidDimensions) {
          result.code = 'INVALID_TASK_OBJECTIVES';
          // Map the invalidDimensions array to a 'dimension' field for our translation
          if (Array.isArray(result.details.invalidDimensions) && result.details.invalidDimensions.length > 0) {
            [result.details.dimension] = result.details.invalidDimensions;
          }
        }

        // Add other specific validation error patterns here as needed
      }
    } else if (error.response?.status === 401) {
      result.code = 'UNAUTHORIZED';
      result.message = 'Your session has expired or you are not authorized to perform this action';
    } else if (error.response?.status === 403) {
      result.code = 'FORBIDDEN';
      result.message = 'You do not have permission to perform this action';
    } else if (error.response?.status === 404) {
      result.code = 'NOT_FOUND';
      result.message = 'The requested resource was not found';
    } else if (error.response?.status >= 500) {
      result.code = 'SERVER_ERROR';
      result.message = 'A server error occurred. Please try again later';
    }

    // Handle FastAPI validation errors
    if (error.response?.data?.detail && Array.isArray(error.response.data.detail)) {
      const validationErrors = error.response.data.detail;
      result.code = 'VALIDATION_ERROR';
      result.message = validationErrors.map((err) => `${err.loc.join('.')}: ${err.msg}`).join(', ');
      result.details = { validationErrors };
    }
  }

  return result;
}

/**
 * Hook to report API errors with toast notifications or persistent dialogs
 */
export function useApiErrorReporter() {
  const { showToastMessage, showDialog } = useDialog();
  const { t } = useTranslation();

  return {
    /**
     * Report API error via toast message or persistent dialog
     * @param error The error from API call
     * @param context Optional context string to prefix the error message
     * @param displayMode Whether to show as 'toast' (default) or 'dialog'
     * @param options Configuration for error reporting behavior
     * @returns Processed error details for further handling
     */
    reportApiError: (
      error: unknown,
      context?: string,
      displayMode: ApiErrorDisplayMode = 'toast',
      options?: ApiErrorReportOptions,
    ) => {
      const errorDetails = processApiError(error);
      const { code, message, details } = errorDetails;

      // Determine if this is an expected error
      const isExpectedError = options?.expectedErrorCodes?.includes(code) ?? false;

      // Log appropriately based on whether error is expected
      if (isExpectedError) {
        log.warn('Expected API validation error:', code, message);
      } else {
        log.error('API Error:', code, message, error);
      }

      // Create translation variables for structured fields (never raw server message in toasts)
      const structuredVars = details || {};
      const dialogTranslationVars = {
        details: message || '',
        ...structuredVars,
      };

      const translationKey = `errors.${code.toLowerCase()}`;

      const toastMessage = t(translationKey, {
        ...structuredVars,
        defaultValue: t('errors.unknown_error'),
      });

      // Dialogs may include processed validation text; toasts stay mapped i18n only.
      const dialogMessage = code === 'VALIDATION_ERROR' && message
        ? `${toastMessage}\n\n${message}`
        : t(translationKey, {
          ...dialogTranslationVars,
          defaultValue: t('errors.unknown_error'),
        });

      const toastDisplayMessage = context ? `${context}: ${toastMessage}` : toastMessage;
      const dialogDisplayMessage = context ? `${context}: ${dialogMessage}` : dialogMessage;

      if (displayMode === 'dialog') {
        // Show persistent dialog that requires user confirmation
        showDialog({
          title: t('errors.error_title', { defaultValue: 'Error' }),
          message: dialogDisplayMessage,
          buttons: [
            {
              text: t('common.ok', { defaultValue: 'OK' }),
              variant: 'primary',
              action: 'confirm',
            },
          ],
        });
      } else {
        // Show toast message (default behavior) — mapped i18n only, no raw API dump
        showToastMessage(
          'error',
          toastDisplayMessage,
          t('errors.could_not_complete_action'),
          'top',
          5000,
        );
      }

      // Return processed error details for additional handling
      return errorDetails;
    },
  };
}
